/**
 * Actions endpoint helpers — pending HITL proposal recovery + status read.
 *
 * Ports v1 ``ando-frontend/app/api/actions.ts`` (lines around the
 * ``listPendingProposals`` call). The BE surface is documented at
 * ``ando-agent/src/api/v1/endpoints/actions.py``.
 *
 * Wire shape:
 *
 *   GET /actions/pending?conversation_id=&limit=
 *     -> { count: number, proposals: Action[] }
 *
 * The proposals payload is the wide v1 ``ActionProposal`` row — domain layer
 * exposes the typed envelope via :mod:`@ando/core/domain` (``Action``); this
 * helper returns the raw list and lets the caller decide whether to parse
 * with the zod schema.
 */

import { apiClient } from "../ApiClient.js";

export interface PendingActionsResponse {
  /** ``proposals.length`` echoed by the BE; kept for parity. */
  readonly count: number;
  readonly proposals: ReadonlyArray<Record<string, unknown>>;
}

export interface GetPendingActionsArgs {
  /** Optional scope: only proposals belonging to this conversation. */
  readonly conversationId?: string;
  /** BE default = 50. Bump on the work-hub hub where we render the queue. */
  readonly limit?: number;
}

/** GET ``/actions/pending`` — recover HITL proposals after a WS disconnect. */
export async function listPendingActions(
  args: GetPendingActionsArgs = {},
): Promise<PendingActionsResponse> {
  const params = new URLSearchParams();
  if (args.conversationId) params.append("conversation_id", args.conversationId);
  if (args.limit !== undefined) params.append("limit", String(args.limit));
  const qs = params.toString();
  return await apiClient.get<PendingActionsResponse>(
    qs.length > 0 ? `/actions/pending?${qs}` : "/actions/pending",
  );
}

// ===========================================================================
// Execute action (HITL confirm)
// ===========================================================================
//
// Wire shape mirrored from
// ``ando-agent/src/api/v1/endpoints/actions.py::execute_action`` +
// ``ExecuteActionRequest`` (legacy body) + ``UnifiedExecuteRequest``
// (May 2026 Phase 6 body).
//
// The legacy executor only consumes ``modifications: Dict[str, Any]`` so we
// pass the FE-edited values under that key — keeps the BE single source of
// truth for the audit-log diff (``proposed_fields`` vs ``executed_fields``)
// without forcing the FE to know which executor path the BE will take.

export interface ExecuteActionRequest {
  /**
   * Last-minute edits the operator made to AI-proposed values, keyed by
   * the same field names that came on ``proposed_fields``. The BE merges
   * these into the executor payload before dispatch and stores both halves
   * in the action audit log.
   */
  readonly modifications?: Readonly<Record<string, unknown>>;
}

/**
 * Machine-readable failure classification (Jul 2026 HITL lifecycle SOTA) —
 * mirrors the BE ``ActionErrorKind`` enum (``src/models/action_models.py``).
 * Drives the card's inline recovery: reconnect / top-up credit / retry /
 * field error, with a generic fallback.
 */
export type ActionErrorKind =
  | "auth_expired"
  | "credit_exhausted"
  | "transient"
  | "validation"
  | "source_unverified"
  | "source_contradicted"
  | "generic";

export interface ExecuteActionResponse {
  readonly success: boolean;
  readonly proposal_id: string;
  readonly action_type: string;
  readonly message: string;
  readonly undo_available: boolean;
  readonly undo_token?: string | null;
  readonly result_data?: Readonly<Record<string, unknown>> | null;
  /**
   * Post-action RECEIPT (Jul 2026) — a normalised deep link to the artefact
   * the executor just produced (created/updated record URL, sent email/message
   * permalink, SDI transmission id, SEPA SCA url, …). ``result_url`` is omitted
   * when a URL can't be constructed → the card falls back to ``external_id`` +
   * ``result_label``. Built BE-side in ``ActionService._build_action_receipt``.
   */
  readonly result_url?: string | null;
  readonly result_label?: string | null;
  readonly external_id?: string | null;
  /**
   * Classified FAILURE (Jul 2026) — present when ``success === false`` because
   * an executor raised. ``error_meta`` carries reconnect hints (e.g.
   * ``{ provider: "salesforce", account_label: "Salesforce" }``).
   */
  readonly error_kind?: ActionErrorKind | null;
  readonly error_meta?: Readonly<Record<string, unknown>> | null;
}

/**
 * POST ``/api/v1/actions/{proposal_id}/execute`` — operator confirms the
 * AI proposal. Body carries the edited field map (optional); the BE
 * audit log records both the AI-proposed and operator-executed sides
 * for compliance (``feedback_hitl_card_editable_fields.md``).
 *
 * NOTE: ``apiClient`` already prefixes ``/api/v1`` via its ``baseUrl``
 * — call sites must pass the path AS IF the prefix is implicit
 * (mirrors every other endpoint in this folder, e.g. ``listPendingActions``
 * uses ``/actions/pending``). The double prefix here used to produce
 * ``POST /api/v1/api/v1/actions/.../execute`` → 404 silent failure
 * (audited May 25, 2026).
 */
/**
 * Executing an action can be LONG-RUNNING — a ``document_create`` deck with
 * per-slide AI illustrations routinely takes 90–150s of synchronous generation.
 * The ApiClient's 120s default abort would kill it mid-flight ("timeout") even
 * though the BE finishes successfully. We give execute a generous ceiling so the
 * legitimate long generation completes; fast actions (email send, etc.) return
 * well under it, so the higher ceiling is harmless to them.
 */
const EXECUTE_TIMEOUT_MS = 300_000; // 5 min — bounds image-heavy deck generation

export async function executeAction(
  proposalId: string,
  body: ExecuteActionRequest = {},
): Promise<ExecuteActionResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<ExecuteActionResponse>(`/actions/${pid}/execute`, body, {
    timeout: EXECUTE_TIMEOUT_MS,
  });
}

// ===========================================================================
// Refine section (document_create partial edit)
// ===========================================================================

export interface RefineSectionRequest {
  /** Heading text identifying the markdown ``##`` section to rewrite. */
  readonly section_heading: string;
  /** Operator-typed free-form rewrite instruction. */
  readonly instruction: string;
}

export interface RefineSectionResponse {
  readonly success: boolean;
  readonly content_preview?: string | null;
  readonly message?: string | null;
}

/**
 * POST ``/actions/{proposal_id}/refine-section`` — surgical rewrite of
 * ONE ``##`` section inside a ``document_create`` proposal's markdown
 * ``content_preview``. The proposal_id is unchanged on success so the
 * audit log captures every refinement on the same lineage.
 */
export async function refineDocumentSection(
  proposalId: string,
  body: RefineSectionRequest,
): Promise<RefineSectionResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<RefineSectionResponse>(`/actions/${pid}/refine-section`, body);
}

// ===========================================================================
// Refine field (generic substring rewrite — InlineAIRefiner)
// ===========================================================================

export interface RefineFieldRequest {
  /** Logical field name (``body``, ``message``, ``description``, …). */
  readonly field_name: string;
  /** The full current text of the field as the FE sees it RIGHT NOW. */
  readonly full_text: string;
  /** Character offset where the user's selection begins. */
  readonly selection_start: number;
  /** Character offset where the user's selection ends (exclusive). */
  readonly selection_end: number;
  /** Operator-typed instruction (or preset like "rendi più formale"). */
  readonly instruction: string;
}

/** One word-level edit turning the selected substring into its rewrite. */
export interface RefineFieldChange {
  readonly id: string;
  readonly type: "insert" | "delete" | "replace";
  readonly before: string;
  readonly after: string;
}

export interface RefineFieldResponse {
  readonly success: boolean;
  readonly new_full_text?: string | null;
  readonly message?: string | null;
  /**
   * Inline-diff payload (Jul 2026, backward-compatible). ``original`` is the
   * SELECTED substring BEFORE the rewrite; ``new_text`` its rewritten form;
   * ``changes`` the word-level diff over those two (NOT the whole body). When
   * present, hosts feed them to ``<InlineAIRefiner submitWithDiff>`` for the
   * Apple-style inline diff; absent → legacy replace-in-place via ``submit``.
   */
  readonly original?: string | null;
  readonly new_text?: string | null;
  readonly changes?: readonly RefineFieldChange[] | null;
}

/**
 * POST ``/actions/{proposal_id}/refine-field`` — generic substring
 * rewriter that powers the ``<InlineAIRefiner>`` floating bubble on every
 * free-text proposal field (email body, message body, PEC body, Salesforce
 * description notes, …). Returns the new ``full_text`` with ONLY the
 * selection rewritten; the surrounding text is preserved verbatim. The
 * proposal_id is unchanged on success — audit lineage stays intact.
 */
export async function refineField(
  proposalId: string,
  body: RefineFieldRequest,
): Promise<RefineFieldResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<RefineFieldResponse>(`/actions/${pid}/refine-field`, body);
}

// ===========================================================================
// Per-slide visual preview (Jul 9, 2026)
// ===========================================================================
//
// A sandbox-authored deck ships a FINISHED .pptx binary (no structured slide
// outline), so its confirm card can't render the editable ``SlideOutlineEditor``
// and falls back to a text summary. This endpoint returns WYSIWYG per-slide PNG
// thumbnails (base64 data URIs) of the ACTUAL rendered deck so the user sees
// the real slides before Confirm. Empty ``slides`` for any non-sandbox /
// non-presentation proposal — the caller keeps the text summary.

export interface SlidePreviewItemWire {
  /** 1-based slide index. */
  readonly index: number;
  /** ``data:image/png;base64,…`` URI of the rendered slide. */
  readonly image: string;
}

export interface SlidePreviewsResponse {
  readonly proposal_id: string;
  readonly slides: readonly SlidePreviewItemWire[];
}

/**
 * GET ``/actions/{proposal_id}/slide-previews`` — WYSIWYG per-slide thumbnails
 * for a sandbox-authored presentation proposal. Fail-open on the BE: any
 * proposal without precomputed deck bytes (or a render failure) yields an
 * empty ``slides`` array.
 */
export async function getSlidePreviews(proposalId: string): Promise<SlidePreviewsResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.get<SlidePreviewsResponse>(`/actions/${pid}/slide-previews`);
}

// ===========================================================================
// Editor-first canvas — finalize / reject (Phase 1.1, May 2026)
// ===========================================================================
//
// New BE state machine for ``document_create`` (and any future
// material-at-proposal-time action): the file is written to the
// ``drafts/`` bucket the moment Ando emits the proposal so the FE editor
// can open it inline. The slim ``<DraftActionBar>`` above the editor
// gives the user a CTA to either:
//
//   - **Finalize** — move bytes to the chosen destination (Drive / OneDrive
//     / Archivio Ando) + flip ``content_items.draft_status`` to
//     ``committed``. Replaces the legacy ``/execute`` path for
//     ``document_create`` proposals.
//   - **Reject** — delete the draft object from MinIO + drop the proposal
//     row. The editor closes, the user is dropped back on the hub.
//
// Both endpoints are idempotent (BE returns 200 on duplicate calls). The
// FE TanStack mutation wrappers live in
// ``apps/client/src/features/actions/hooks/useFinalizeAction.ts``.

export interface FinalizeActionRequest {
  /**
   * Identifier of the chosen destination — must match one of the
   * ``EditSessionActionState.destinations[].id`` slots the BE emitted
   * when the draft was materialised. The BE rejects unknown ids with
   * 422.
   */
  readonly destination_id: string;
  /**
   * Optional last-minute edits the operator made to AI-proposed values
   * (mirrors ``ExecuteActionRequest.modifications``). Empty for the
   * canonical "user just clicked Conferma without editing" path. Kept
   * for future parity with the legacy ``/execute`` audit log.
   */
  readonly modifications?: Readonly<Record<string, unknown>>;
}

export interface FinalizeActionResponse {
  readonly success: boolean;
  readonly proposal_id: string;
  readonly doc_id: string;
  /** The destination id the BE actually used (echoes the request). */
  readonly destination_id: string;
  /** Optional human-readable confirmation message. */
  readonly message?: string | null;
}

/**
 * POST ``/api/v1/actions/{proposal_id}/finalize`` — commits an AI draft
 * to the chosen destination. Replaces ``/execute`` for ``document_create``
 * proposals. On success the BE emits an ``action_status: executed`` WS
 * event AND the next ``GET /documents/{doc_id}/edit-session`` returns
 * ``action_state: null`` (or ``draft_status: "committed"``) so the FE
 * ``<DraftActionBar>`` unmounts automatically when TanStack invalidates
 * the cache.
 */
export async function finalizeAction(
  proposalId: string,
  body: FinalizeActionRequest,
): Promise<FinalizeActionResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<FinalizeActionResponse>(`/actions/${pid}/finalize`, body);
}

export interface RejectActionResponse {
  readonly success: boolean;
  readonly proposal_id: string;
  readonly message?: string | null;
}

// ===========================================================================
// Unified payload schema wire shape (canonical BE contract — June 2026)
// ===========================================================================
//
// Single source of truth for the ``GET /actions/{id}/payload-schema`` AND
// the manual-create / ai-fill responses below. Mirrors
// ``ando-agent/src/services/actions/field_spec.py::UnifiedPayloadSchema``.
// ``apps/client``'s ``usePayloadSchema`` imports these instead of redefining
// them (DRY).

export interface EmailSenderOptionMetadata {
  readonly label?: string;
  readonly email_address?: string;
  readonly connection_type?: string;
  readonly access_mode?: "workspace" | "send_only";
  readonly can_read?: boolean;
  readonly can_sync?: boolean;
  readonly can_send?: boolean;
  readonly can_autocomplete?: boolean;
  readonly can_reply?: boolean;
  readonly can_forward?: boolean;
  readonly can_schedule?: boolean;
}

export interface UnifiedFieldSpecWire {
  readonly option_metadata?: Readonly<Record<string, EmailSenderOptionMetadata>>;
  readonly name: string;
  readonly label: string;
  readonly type: string;
  readonly required: boolean;
  readonly readonly: boolean;
  readonly value: unknown;
  /**
   * The record's CURRENT value (the diff "before") for UPDATE_RECORD proposals.
   * Present only when the BE could read the live record; ``null`` / absent
   * otherwise — the card then shows just the proposed value (no diff). Mirrors
   * :pyattr:`UnifiedFieldSpec.current_value` BE-side.
   */
  readonly current_value?: unknown;
  readonly proposed_by_ai: boolean;
  readonly is_action_time: boolean;
  readonly help_text?: string;
  readonly options?: readonly string[];
  readonly max_length?: number | null;
  readonly default_value?: unknown;
  readonly reference_to?: string | null;
  readonly validation?: {
    readonly pattern?: string | null;
    readonly max_length?: number | null;
  } | null;
  /**
   * For ``recipients`` fields: name of the per-capability account-picker
   * field elsewhere in this schema whose value supplies the connection
   * id for the people-search autocomplete (e.g. ``from_account`` for
   * email send, ``calendar_connection_id`` for calendar create event).
   * Mirrors :pyattr:`UnifiedFieldSpec.people_search_connection_field_name`
   * BE-side. Optional — when omitted the FE chip input falls back to
   * free-text mode.
   */
  readonly people_search_connection_field_name?: string | null;
}

export interface UnifiedPayloadSchemaWire {
  readonly action_id: string;
  readonly action_type: string;
  readonly provider?: string | null;
  readonly sobject_type?: string | null;
  readonly fields: readonly UnifiedFieldSpecWire[];
}

// ===========================================================================
// Manual action creation + AI-assisted fill (June 2026)
// ===========================================================================
//
// Lets the user create ANY action by hand from the working area — not just
// react to an AI proposal — mirroring the email composer. Both endpoints
// return the SAME unified payload schema as ``GET /actions/{id}/payload-schema``
// so the FE renders the identical ``<ActionFieldEditor>``. BE:
// ``ando-agent/src/api/v1/endpoints/actions.py::create_action`` + ``ai_fill_action``.

export interface CreateActionRequest {
  /** Canonical ``ActionType`` enum value (e.g. ``"fatture_in_cloud_action"``). */
  readonly action_type: string;
  /** Registry key when action_type is native_capability. */
  readonly capability_key?: string;
  /** Vendor/provider hint (e.g. ``"fatture_in_cloud"``, ``"salesforce"``, ``"slack"``). */
  readonly provider?: string;
  readonly connector?: string;
  /** Target sobject (CRM record create) when applicable. */
  readonly sobject_type?: string;
  /** Vendor sub-type (e.g. FiC ``"create_invoice"``, SF ``"create_lead"``). */
  readonly specialized_action_type?: string;
  /** Pre-filled values (may be empty — the form starts blank otherwise). */
  readonly initial_fields?: Readonly<Record<string, unknown>>;
  /** Required for UPDATE_RECORD (the row being edited). */
  readonly record_id?: string;
  readonly conversation_id?: string;
}

export interface NativeActionCapability {
  readonly key: string;
  readonly version: number;
  readonly label: string;
  readonly description: string;
  readonly domain: string;
  readonly area: string;
  readonly icon: string;
  readonly risk: "low" | "medium" | "high" | "critical";
  readonly params_schema: Readonly<Record<string, unknown>>;
}

export async function getActionCatalog(): Promise<{ capabilities: NativeActionCapability[] }> {
  return await apiClient.get<{ capabilities: NativeActionCapability[] }>("/actions/catalog");
}

export interface ManualActionResponse {
  readonly proposal_id: string;
  readonly action_type: string;
  readonly payload_schema: UnifiedPayloadSchemaWire;
}

/**
 * POST ``/api/v1/actions`` — mint a manual action proposal in
 * ``awaiting_confirmation`` for ``action_type`` (+ optional provider /
 * sub-type / initial fields). Returns the proposal id and its unified
 * payload schema so the FE can immediately render the editor. The BE
 * returns HTTP 422 (with a ``detail`` listing supported types) for an
 * action type not yet wired for manual creation.
 */
export async function createAction(body: CreateActionRequest): Promise<ManualActionResponse> {
  return await apiClient.post<ManualActionResponse>("/actions", body);
}

export interface AiFillRequest {
  /** Free-text intent, e.g. "fattura a Rossi Srl per 3 ore a 80€/h". */
  readonly instruction: string;
  /** Optional source doc/thread ids for grounding. */
  readonly context_refs?: ReadonlyArray<string>;
}

/**
 * POST ``/api/v1/actions/{proposal_id}/ai-fill`` — the "Compila con AI"
 * button on the manual composer. The BE runs the existing Vertex AI
 * pipeline to populate the form's fields from ``instruction`` (additive —
 * it never overwrites values the operator already set), and returns the
 * updated unified payload schema so the FE re-renders the populated form.
 * High-risk action-time fields (e.g. FiC ``recipient_email``) stay blank
 * for operator verification — the model is never asked to guess them.
 */
export async function aiFillAction(
  proposalId: string,
  body: AiFillRequest,
): Promise<ManualActionResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<ManualActionResponse>(`/actions/${pid}/ai-fill`, body);
}

/**
 * POST ``/api/v1/actions/{proposal_id}/reject`` — discards a pending AI
 * draft. The BE deletes the ``drafts/`` MinIO object + marks the
 * proposal row ``status='rejected'`` (audit log preserved). Idempotent —
 * re-calling on an already-rejected proposal returns 200.
 */
export async function rejectAction(proposalId: string): Promise<RejectActionResponse> {
  const pid = encodeURIComponent(proposalId);
  return await apiClient.post<RejectActionResponse>(`/actions/${pid}/reject`, {});
}

// ---------------------------------------------------------------------------
// Attachable files — Ando-document picker for email/message attachments.
// ---------------------------------------------------------------------------

/**
 * One attachable file from the user's unified content index (Drive,
 * uploads, email attachments, AI-generated docs). ``dedup_id`` is the
 * canonical ``content_items.id`` — pass it back as an attachment's
 * ``content_item_id`` and the BE resolves it to bytes at send time
 * (``_execute_send_email`` → ``fetch_email_attachment_payload``).
 */
export interface AttachableFile {
  readonly dedup_id: string;
  readonly filename: string;
  readonly mime_type?: string | null;
  readonly file_size?: number | null;
  readonly source_type?: string | null;
  readonly source_provider?: string | null;
  readonly source_label?: string | null;
  readonly size_display?: string | null;
}

interface AttachableFilesResponse {
  readonly files: ReadonlyArray<AttachableFile>;
  readonly count: number;
  readonly search_term?: string | null;
}

export interface SearchAttachableFilesParams {
  /** Filename search term (case-insensitive). Omit to list recent files. */
  readonly q?: string;
  /** Filter by source: ``drive`` | ``manual_upload`` | ``email_attachment``. */
  readonly sourceType?: string;
  readonly limit?: number;
  readonly signal?: AbortSignal;
}

/**
 * ``GET /api/v1/actions/attachments/search`` — list/search the user's
 * attachable files for an email/message attachment picker. DRY: reuses
 * the SAME endpoint the agent's ``search_attachable_files`` tool calls,
 * so the picker and the AI agree on what's attachable.
 */
export async function searchAttachableFiles(
  params: SearchAttachableFilesParams = {},
): Promise<ReadonlyArray<AttachableFile>> {
  const { q, sourceType, limit, signal } = params;
  const qs = new URLSearchParams();
  if (q?.trim()) qs.set("q", q.trim());
  if (sourceType) qs.set("source_type", sourceType);
  if (typeof limit === "number") qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await apiClient.get<AttachableFilesResponse>(
    `/actions/attachments/search${suffix}`,
    signal ? { signal } : undefined,
  );
  return res.files ?? [];
}
